import { ReactElement, useMemo } from 'react'
import {
  ColorRepresentation,
  DoubleSide,
  Shape,
  Matrix4,
  EdgesGeometry,
  ExtrudeGeometry,
  Color,
} from 'three'
import { LineSegments2, LineMaterial, LineSegmentsGeometry } from 'three-stdlib'
import { useTheme } from '@mui/material'
import { BasicMesh, BasicMeshProps, ImmutableVector3, sceneConfig } from '@modugen/ui-toolkit'
import { useDisposeThreeResourceOnUnmount } from '@editorHooks'
import {
  createIHProfileShape,
  createUProfileShape,
  createCircularHollowShape,
  createRectangularHollowShape,
  createRectangularShape,
} from '../steelProfileUtils'
import {
  getShapeMatrixTransform,
  getShapeObject,
  getShapeEdges,
  createMeshStandardMaterialMemoized,
  extrudePosition,
} from '../utils'

interface Props extends BasicMeshProps {
  start: ImmutableVector3
  end: ImmutableVector3
  coordinateSystem: CoordinateSystem
  height: number
  width: number

  color?: string | ColorRepresentation
  outlinesColor?: string | ColorRepresentation
  outlines?: boolean
  lineWidth?: number

  opacity?: number

  // Steel profile specific props
  crossSectionShape?: SteelCSShape | RectangularCSShape
}

const MemberMeshElement = ({
  start,
  end,
  coordinateSystem,
  height,
  width,

  color,
  outlinesColor,
  outlines = true,
  lineWidth = 1,

  opacity = 1,

  crossSectionShape,

  ...props
}: Props): ReactElement => {
  const theme = useTheme()

  // Determine if this is a steel profile and get its type
  const isSteelProfile = crossSectionShape?.kind_literal === 'SteelCS'
  const profileType = isSteelProfile ? (crossSectionShape as SteelCSShape).profile_type : undefined

  // Create the profile shape based on type
  const profileShape2D = useMemo((): Shape => {
    if (!isSteelProfile || !profileType || !crossSectionShape) {
      // Fallback to rectangular shape
      return createRectangularShape(width, height)
    }

    const steelShape = crossSectionShape as SteelCSShape

    switch (profileType) {
      case 'IAndHSection':
        // I or H profile
        if (steelShape.thickness_flange && steelShape.thickness_web) {
          return createIHProfileShape(
            width,
            height,
            steelShape.thickness_flange,
            steelShape.thickness_web,
            steelShape.radius,
          )
        }
        break

      case 'UPNSection':
      case 'UPESection':
        // U profile
        if (steelShape.thickness_flange && steelShape.thickness_web) {
          return createUProfileShape(
            width,
            height,
            steelShape.thickness_flange,
            steelShape.thickness_web,
          )
        }
        break

      case 'ROSection':
        // Circular hollow section
        if (steelShape.diameter && steelShape.thickness) {
          return createCircularHollowShape(steelShape.diameter, steelShape.thickness)
        }
        break

      case 'RROAndQROSection':
        // Rectangular/Square hollow section
        if (steelShape.thickness) {
          return createRectangularHollowShape(width, height, steelShape.thickness)
        }
        break
    }

    // Fallback to rectangular if profile data is incomplete
    return createRectangularShape(width, height)
  }, [isSteelProfile, profileType, crossSectionShape, width, height])

  // Create transformation matrix to orient the profile along the beam axis
  const beamLength = useMemo(() => end.sub(start).length(), [start, end])

  const matrixTransform = useMemo(() => {
    const beamDirection = end.sub(start).normalize()
    const xDir = coordinateSystem.y_direction.v // Width direction
    const yDir = coordinateSystem.z_direction.v // Height direction
    const zDir = beamDirection.v // Extrusion direction

    const transform = new Matrix4().makeBasis(xDir, yDir, zDir)
    transform.setPosition(start.v)

    return transform
  }, [start, end, coordinateSystem])

  // For rectangular profiles, use the old method
  const points = useMemo(
    () => extrudePosition(start, end, coordinateSystem, height, width),
    [start, end, coordinateSystem, height, width],
  )

  const rectangularShapeObject = useMemo(
    () => (!isSteelProfile ? getShapeObject(points, [], getShapeMatrixTransform(points)) : null),
    [points, isSteelProfile],
  )

  // Create extrude geometry
  const extrudeGeometry = useMemo(() => {
    return new ExtrudeGeometry(profileShape2D, {
      depth: beamLength,
      bevelEnabled: false,
    })
  }, [profileShape2D, beamLength])

  useDisposeThreeResourceOnUnmount(extrudeGeometry)

  const edges = useMemo(() => {
    if (!isSteelProfile && rectangularShapeObject) {
      return getShapeEdges(rectangularShapeObject, height)
    }
    // For steel profiles, create edges from the extrude geometry
    return new EdgesGeometry(extrudeGeometry, 15)
  }, [isSteelProfile, rectangularShapeObject, height, extrudeGeometry])

  useDisposeThreeResourceOnUnmount(edges)

  // Convert EdgesGeometry to LineSegmentsGeometry for LineSegments2
  const lineSegmentsGeometry = useMemo(() => {
    const lineGeometry = new LineSegmentsGeometry()
    const positions = edges.attributes.position.array
    lineGeometry.setPositions(Array.from(positions))
    return lineGeometry
  }, [edges])

  useDisposeThreeResourceOnUnmount(lineSegmentsGeometry)

  const material = useMemo(
    () =>
      createMeshStandardMaterialMemoized({
        side: DoubleSide,
        color: (color || theme.scenePalette.elements3d.member) as ColorRepresentation,
        transparent: true,
        opacity,
      }),
    [color, theme.scenePalette.elements3d.member, opacity],
  )

  useDisposeThreeResourceOnUnmount(material)

  const outlinesMaterial = useMemo(() => {
    const color = new Color(outlinesColor || theme.scenePalette.outlines)
    const lineMaterial = new LineMaterial({
      color: color.getHex(),
      linewidth: lineWidth,
      transparent: true,
      opacity,
    })
    // Set resolution (typically based on window size, but we'll use a reasonable default)
    lineMaterial.resolution.set(window.innerWidth, window.innerHeight)
    return lineMaterial
  }, [outlinesColor, theme.scenePalette.outlines, opacity, lineWidth])

  useDisposeThreeResourceOnUnmount(outlinesMaterial)

  // Use different rendering approach for steel profiles vs rectangular
  if (isSteelProfile) {
    const lineSegments2Steel = new LineSegments2(lineSegmentsGeometry, outlinesMaterial)
    lineSegments2Steel.layers.set(sceneConfig.R3FNonSelectableObjectLayer)
    lineSegments2Steel.renderOrder = 100
    lineSegments2Steel.computeLineDistances()

    return (
      <BasicMesh matrix={matrixTransform} matrixAutoUpdate={false} material={material} {...props}>
        <primitive object={extrudeGeometry} attach="geometry" />

        {outlines && <primitive object={lineSegments2Steel} visible={props.visible} />}
      </BasicMesh>
    )
  }

  // Fallback to original rectangular rendering
  const rectangularMatrix = getShapeMatrixTransform(points)
  const lineSegments2Rectangular = new LineSegments2(lineSegmentsGeometry, outlinesMaterial)
  lineSegments2Rectangular.layers.set(sceneConfig.R3FNonSelectableObjectLayer)
  lineSegments2Rectangular.renderOrder = 100
  lineSegments2Rectangular.computeLineDistances()

  return (
    <BasicMesh matrix={rectangularMatrix} matrixAutoUpdate={false} material={material} {...props}>
      <extrudeGeometry
        args={[
          rectangularShapeObject!,
          {
            depth: height,
            bevelEnabled: false,
          },
        ]}
      />

      {outlines && <primitive object={lineSegments2Rectangular} visible={props.visible} />}
    </BasicMesh>
  )
}

export default MemberMeshElement
